import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { gsap, useGSAP, prefersReducedMotion } from '@/lib/gsap'
import { Button } from '@/components/generals/button'
import { Container } from '@/components/generals/container'
import { TransitionLink } from '@/components/generals/transition-link'

import { NAV_LINKS, PRIMARY_CTA, siteConfig } from '@/constants/site'

interface MobileMenuProps {
    open: boolean
    activePath: string
    onClose: () => void
}

export const MobileMenu: FC<MobileMenuProps> = ({ open, activePath, onClose }): ReactNode => {
    const rootRef = useRef<HTMLDivElement>(null)
    const firstRunRef = useRef(true)

    useGSAP(() => {
        const root = rootRef.current

        if (!root) return

        if (firstRunRef.current) {
            firstRunRef.current = false
            return
        }

        const links = root.querySelectorAll('[data-menu-link]')
        const extras = root.querySelectorAll('[data-menu-extra]')

        if (prefersReducedMotion()) {
            gsap.set(root, { autoAlpha: open ? 1 : 0, clipPath: 'inset(0% 0% 0% 0%)' })
            return
        }

        if (open) {
            gsap.timeline()
                .set(root, { autoAlpha: 1 })
                .fromTo(root, { clipPath: 'inset(0% 0% 100% 0%)' }, {
                    clipPath: 'inset(0% 0% 0% 0%)',
                    duration: 0.9,
                    ease: 'expo.inOut'
                })
                .fromTo(links, { yPercent: 115 }, {
                    yPercent: 0,
                    duration: 1,
                    stagger: 0.06,
                    ease: 'expo.out'
                }, '-=0.45')
                .fromTo(extras, { autoAlpha: 0, y: 16 }, {
                    autoAlpha: 1,
                    y: 0,
                    duration: 0.8,
                    stagger: 0.06,
                    ease: 'expo.out'
                }, '-=0.8')

            return
        }

        gsap.timeline()
            .to(links, {
                yPercent: -115,
                duration: 0.45,
                stagger: 0.03,
                ease: 'power3.in'
            })
            .to(root, {
                clipPath: 'inset(0% 0% 100% 0%)',
                duration: 0.75,
                ease: 'expo.inOut'
            }, '-=0.25')
            .set(root, { autoAlpha: 0 })
    }, { dependencies: [open], scope: rootRef })

    return (
        <div
            ref={rootRef}
            id='mobile-menu'
            role='dialog'
            aria-modal='true'
            aria-label='Site menu'
            inert={!open}
            className='invisible fixed inset-0 z-[45] flex flex-col bg-background opacity-0 lg:hidden'
        >
            <Container className='flex h-full flex-col pt-[104px] pb-8'>
                <nav aria-label='Mobile' className='flex-1'>
                    <ul className='flex flex-col'>
                        {NAV_LINKS.map((link, index) => {
                            const active = activePath === link.href || activePath.startsWith(`${link.href}/`)

                            return (
                                <li key={link.href} className='border-b border-line'>
                                    <TransitionLink
                                        href={link.href}
                                        onClick={onClose}
                                        className='group flex items-baseline justify-between overflow-hidden py-4'
                                    >
                                        <span
                                            data-menu-link
                                            className={cn(
                                                'block font-heading text-[clamp(2.25rem,10vw,3.5rem)] leading-[1.05] tracking-[-0.045em] transition-colors',
                                                active ? 'text-foreground' : 'text-foreground/70 group-hover:text-foreground'
                                            )}
                                        >
                                            {link.label}
                                        </span>

                                        <span data-menu-link className='type-label block text-muted-foreground'>
                                            {active ? 'Current' : `0${index + 1}`}
                                        </span>
                                    </TransitionLink>
                                </li>
                            )
                        })}
                    </ul>
                </nav>

                <div className='flex flex-col gap-6 pt-10'>
                    <div data-menu-extra>
                        <Button href={PRIMARY_CTA.href} onClick={onClose} className='w-full justify-between'>
                            {PRIMARY_CTA.label}
                        </Button>
                    </div>

                    <div data-menu-extra className='flex items-end justify-between gap-4'>
                        <a href={`mailto:${siteConfig.email}`} className='text-soft transition-colors hover:text-foreground'>
                            {siteConfig.email}
                        </a>

                        <span className='type-label text-right text-muted-foreground'>
                            AI automation
                            <br />
                            and intelligent systems
                        </span>
                    </div>
                </div>
            </Container>
        </div>
    )
}
